import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { liveCallsStyles } from '../../styles/live-calls-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { callsService } from '../../services/calls-service.js';
import { twilioService } from '../../services/twilio-service.js';
import { liveCallsService } from '../../services/live-calls-service.js';
import { confirmService } from '../../services/confirm-service.js';
import { tz } from '../../services/timezone-service.js';

export class LiveCallsMonitor extends LitElement {
  static properties = {
    calls: { type: Array },
    connected: { type: Boolean },
    _selectedCallId: { state: true },
    _filter: { state: true },
    _searchQuery: { state: true },
    _now: { state: true },
    _showNotes: { state: true },
    _notes: { state: true },
    _noteText: { state: true },
    _notesLoading: { state: true },
    _showFlagMenu: { state: true },
    _flagging: { state: true },
    _hangingUp: { state: true },
  };

  static styles = [designSystem, componentStyles, liveCallsStyles];

  constructor() {
    super();
    this.calls = [];
    this.connected = false;
    this._selectedCallId = null;
    this._filter = 'all';
    this._searchQuery = '';
    this._now = Date.now();
    this._timerInterval = null;
    this._showNotes = false;
    this._notes = [];
    this._noteText = '';
    this._notesLoading = false;
    this._showFlagMenu = false;
    this._flagging = false;
    this._hangingUp = false;
    this._transcriptAtBottom = true;
    this._lastTranscriptCount = 0;
  }

  connectedCallback() {
    super.connectedCallback();
    // Update _now every second to drive live duration display
    this._timerInterval = setInterval(() => {
      this._now = Date.now();
    }, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._timerInterval) {
      clearInterval(this._timerInterval);
      this._timerInterval = null;
    }
  }

  updated(changedProps) {
    super.updated(changedProps);
    // Auto-scroll transcript if user is at the bottom
    const call = this._selectedCall;
    const count = call?.transcript?.length || 0;
    if (count > this._lastTranscriptCount) {
      this._lastTranscriptCount = count;
      if (this._transcriptAtBottom) {
        this._scrollTranscriptToBottom();
      }
    }
  }

  _scrollTranscriptToBottom() {
    requestAnimationFrame(() => {
      const el = this.renderRoot?.querySelector('.transcript-area');
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  _onTranscriptScroll(e) {
    const el = e.target;
    // Consider "at bottom" if within 40px of the end
    this._transcriptAtBottom = (el.scrollHeight - el.scrollTop - el.clientHeight) < 40;
  }

  get _filteredCalls() {
    let filtered = this.calls || [];

    // Apply status filter
    if (this._filter !== 'all') {
      filtered = filtered.filter(c => c.status === this._filter);
    }

    // Apply search
    if (this._searchQuery.trim()) {
      const q = this._searchQuery.trim().toLowerCase();
      filtered = filtered.filter(c =>
        (c.phone_number || '').toLowerCase().includes(q) ||
        (c.agent_name || '').toLowerCase().includes(q) ||
        (c.call_sid || '').toLowerCase().includes(q)
      );
    }

    return filtered;
  }

  get _selectedCall() {
    if (!this._selectedCallId) return null;
    return (this.calls || []).find(c => c.id === this._selectedCallId) || null;
  }

  get _statusCounts() {
    const calls = this.calls || [];
    const counts = { all: calls.length, active: 0 };
    for (const call of calls) {
      if (call.status === 'active') counts.active++;
    }
    return counts;
  }

  render() {
    const counts = this._statusCounts;

    return html`
      ${iconStyles}

      <!-- Page Header -->
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">
            Live Calls
            <span class="connection-dot ${this.connected ? 'connected' : 'disconnected'}"
                  title="${this.connected ? 'Connected' : 'Disconnected'}"></span>
          </h1>
          <p class="page-description">Monitor active calls in real-time</p>
        </div>
      </div>

      <div class="monitor-layout">
        <!-- Left Panel -->
        <div class="left-panel">
          <div class="left-header">
            <!-- Filter Tabs -->
            <div class="filter-tabs">
              ${this._renderFilterTab('all', 'All', counts.all)}
              ${this._renderFilterTab('active', 'Active', counts.active)}
            </div>

            <!-- Search -->
            <div class="search-box">
              <i class="fa-sharp fa-light fa-magnifying-glass search-icon"></i>
              <input
                type="text"
                placeholder="Search by phone number..."
                .value=${this._searchQuery}
                @input=${(e) => { this._searchQuery = e.target.value; }}
              />
            </div>
          </div>

          <!-- Call List -->
          <div class="call-list">
            ${this._filteredCalls.length > 0
              ? this._filteredCalls.map(call => this._renderCallCard(call))
              : this._renderEmptyList()
            }
          </div>
        </div>

        <!-- Right Panel -->
        <div class="right-panel">
          ${this._selectedCall
            ? this._renderCallDetail(this._selectedCall)
            : this._renderEmptyDetail()
          }
        </div>
      </div>

    `;
  }

  _renderEmptyList() {
    return html`
      <div class="empty-panel" style="padding: var(--space-8); height: auto;">
        <div class="empty-icon" style="width: 48px; height: 48px; font-size: 1.2rem;">
          <i class="fa-sharp fa-light ${this._searchQuery ? 'fa-phone-slash' : 'fa-phone-arrow-down-left'}"></i>
        </div>
        <div class="empty-title" style="font-size: var(--text-base);">
          ${this._searchQuery ? 'No Calls Found' : 'No Active Calls'}
        </div>
        <div class="empty-desc" style="font-size: var(--text-xs);">
          ${this._searchQuery
            ? 'No calls match your search'
            : 'Waiting for incoming calls...'}
        </div>
      </div>
    `;
  }

  _renderFilterTab(value, label, count) {
    const isActive = this._filter === value;
    return html`
      <button
        class="filter-tab ${isActive ? 'active' : ''}"
        @click=${() => { this._filter = value; }}
      >
        ${label}<span class="tab-count">${count}</span>
      </button>
    `;
  }

  _renderCallCard(call) {
    const isSelected = this._selectedCallId === call.id;
    const duration = this._calcDuration(call.started_at);

    return html`
      <div
        class="call-card ${isSelected ? 'selected' : ''}"
        @click=${() => { this._selectedCallId = call.id; this._transcriptAtBottom = true; this._lastTranscriptCount = 0; }}
      >
        <div class="call-card-top">
          <span class="call-card-phone">${this._formatPhone(call.phone_number)}</span>
          <span class="call-card-badges">
            ${this._getCallFlag(call) ? html`
              <span class="call-card-badge flag-${this._getCallFlag(call).type}">
                <i class="fa-sharp fa-solid fa-flag" style="font-size: 0.6rem;"></i>
                ${this._getCallFlag(call).type}
              </span>
            ` : ''}
            <span class="call-card-badge active">
              <span class="badge-dot"></span>
              Active
            </span>
          </span>
        </div>
        <div class="call-card-meta">
          <span class="call-card-meta-item">
            <i class="fa-sharp fa-light fa-robot"></i>
            ${call.agent_name || 'AI Agent'}
          </span>
          <span class="call-card-meta-item call-card-duration">
            <i class="fa-sharp fa-light fa-timer"></i>
            ${this._formatDuration(duration)}
          </span>
          <span class="call-card-meta-item">
            <i class="fa-sharp fa-light fa-message-lines"></i>
            ${(call.transcript || []).length}
          </span>
        </div>
        ${this._renderPipelineBadge(call.pipeline_state, true)}
      </div>
    `;
  }

  _renderCallDetail(call) {
    const duration = this._calcDuration(call.started_at);

    return html`
      <!-- Detail Header -->
      <div class="detail-header">
        <div class="detail-header-top">
          <span class="detail-phone">${this._formatPhone(call.phone_number)}</span>
          <div class="detail-badges">
            <span class="detail-live-badge">
              <span class="detail-live-dot"></span>
              Live
            </span>
            ${this._renderPipelineBadge(call.pipeline_state, false)}
          </div>
        </div>
        <div class="detail-meta">
          <span class="detail-meta-item">
            <i class="fa-sharp fa-light fa-robot"></i>
            <span class="detail-meta-value">${call.agent_name || 'AI Agent'}</span>
          </span>
          <span class="detail-meta-item">
            <i class="fa-sharp fa-light fa-fingerprint"></i>
            <span class="detail-meta-value mono">${call.call_sid || '--'}</span>
          </span>
          <span class="detail-meta-item">
            <i class="fa-sharp fa-light fa-clock"></i>
            <span class="detail-meta-value">Started ${this._formatTime(call.started_at)}</span>
          </span>
          <span class="detail-meta-item">
            <i class="fa-sharp fa-light fa-timer"></i>
            <span class="detail-meta-value mono">${this._formatDuration(duration)}</span>
          </span>
        </div>
      </div>

      <!-- Transcript -->
      <div class="transcript-area" @scroll=${this._onTranscriptScroll}>
        ${call.transcript && call.transcript.length > 0
          ? call.transcript.map(msg => this._renderTranscriptMsg(msg, call.started_at))
          : html`
            <div class="empty-panel" style="height: auto; padding: var(--space-12);">
              <div class="empty-icon" style="width: 56px; height: 56px; font-size: 1.5rem;">
                <i class="fa-sharp fa-light fa-message-dots"></i>
              </div>
              <div class="empty-title" style="font-size: var(--text-base);">Waiting for Conversation</div>
              <div class="empty-desc">Transcript will appear here once the call connects</div>
            </div>
          `
        }
      </div>

      <!-- Actions Bar -->
      <div class="actions-bar">
        <button class="action-btn hangup-btn"
          @click=${() => this._confirmHangup(call)}
          ?disabled=${this._hangingUp || !call.call_sid}
          title="${!call.call_sid ? 'No Twilio SID available' : 'Hang up this call'}">
          ${this._hangingUp ? html`
            <i class="fa-sharp fa-light fa-spinner-third fa-spin"></i>
            Ending...
          ` : html`
            <i class="fa-sharp fa-light fa-phone-hangup"></i>
            Hang Up
          `}
        </button>
        <button class="action-btn" title="Coming soon" disabled>
          <i class="fa-sharp fa-light fa-ear-listen"></i>
          Whisper
        </button>
        <button class="action-btn" title="Coming soon" disabled>
          <i class="fa-sharp fa-light fa-arrow-right-arrow-left"></i>
          Transfer
        </button>
        <div class="actions-spacer"></div>
        <div class="flag-btn-wrap">
          <button class="action-btn ${this._getCallFlag(call) ? 'flagged' : 'danger'}"
            @click=${(e) => this._toggleFlagMenu(e, call)}>
            <i class="fa-sharp fa-${this._getCallFlag(call) ? 'solid' : 'light'} fa-flag"></i>
            ${this._getCallFlag(call) ? this._getCallFlag(call).type : 'Flag'}
          </button>
          ${this._showFlagMenu ? this._renderFlagMenu(call) : ''}
        </div>
        <button class="action-btn ${this._showNotes ? 'active' : ''}"
          @click=${() => this._toggleNotes(call)}>
          <i class="fa-sharp fa-light fa-note-sticky"></i>
          Notes${(this._notes || []).length > 0 ? ` (${this._notes.length})` : ''}
        </button>
      </div>

      <!-- Notes Panel -->
      ${this._showNotes ? this._renderNotesPanel(call) : ''}
    `;
  }

  _renderTranscriptMsg(msg, startedAt) {
    const isAgent = msg.speaker === 'agent';
    const cssClass = isAgent ? 'ai' : 'caller';
    const timeStr = this._formatTimestampMs(msg.timestamp_ms, startedAt);

    return html`
      <div class="transcript-message ${cssClass}">
        <span class="transcript-label">
          ${isAgent
            ? html`<i class="fa-sharp fa-light fa-robot"></i> AI Agent`
            : html`<i class="fa-sharp fa-light fa-user"></i> Caller`
          }
        </span>
        <div class="transcript-bubble">${msg.content}</div>
        <span class="transcript-time">${timeStr}</span>
      </div>
    `;
  }

  _renderEmptyDetail() {
    const hasCalls = (this.calls || []).length > 0;

    return html`
      <div class="empty-panel">
        <div class="empty-icon">
          <i class="fa-sharp fa-light ${hasCalls ? 'fa-phone-arrow-up-right' : 'fa-satellite-dish'}"></i>
        </div>
        <div class="empty-title">
          ${hasCalls ? 'Select a call to view details' : 'Listening for calls'}
        </div>
        <div class="empty-desc">
          ${hasCalls
            ? 'Choose a call from the list on the left to see the live transcript, caller information, and available actions.'
            : 'When an incoming call arrives, it will appear here in real-time with a live transcript.'}
        </div>
      </div>
    `;
  }

  // ── Notes ──

  async _toggleNotes(call) {
    this._showNotes = !this._showNotes;
    this._showFlagMenu = false;
    if (this._showNotes) {
      await this._loadNotes(call.id);
    }
  }

  async _loadNotes(callId) {
    this._notesLoading = true;
    try {
      const result = await callsService.loadNotes(callId);
      this._notes = result.success ? result.notes : [];
    } catch (e) {
      this._notes = [];
    } finally {
      this._notesLoading = false;
    }
  }

  async _addNote(call) {
    const text = this._noteText.trim();
    if (!text) return;
    try {
      const result = await callsService.addNote(call.id, text);
      if (result.success) {
        this._noteText = '';
        await this._loadNotes(call.id);
      }
    } catch (e) { /* silent */ }
  }

  async _deleteNote(call, index) {
    try {
      const result = await callsService.deleteNote(call.id, index);
      if (result.success) {
        await this._loadNotes(call.id);
      }
    } catch (e) { /* silent */ }
  }

  _renderNotesPanel(call) {
    return html`
      <div class="notes-panel">
        <div class="notes-header">
          <span class="notes-title">
            <i class="fa-sharp fa-light fa-note-sticky"></i>
            Notes
          </span>
          <button class="notes-close" @click=${() => { this._showNotes = false; }}>
            <i class="fa-sharp fa-light fa-xmark"></i>
          </button>
        </div>

        <div class="notes-list">
          ${this._notesLoading ? html`
            <div class="notes-loading">Loading notes...</div>
          ` : (this._notes || []).length === 0 ? html`
            <div class="notes-empty">No notes yet. Add one below.</div>
          ` : this._notes.map((note, i) => html`
            <div class="note-item">
              <div class="note-content">${note.content}</div>
              <div class="note-meta">
                <span class="note-author">${note.author}</span>
                <span class="note-time">${this._formatNoteTime(note.timestamp)}</span>
                <button class="note-delete" @click=${() => this._deleteNote(call, i)}
                  title="Delete note">
                  <i class="fa-sharp fa-light fa-trash-can"></i>
                </button>
              </div>
            </div>
          `)}
        </div>

        <div class="notes-input-row">
          <input class="notes-input" type="text"
            placeholder="Add a note..."
            .value=${this._noteText}
            @input=${(e) => { this._noteText = e.target.value; }}
            @keydown=${(e) => { if (e.key === 'Enter') this._addNote(call); }}
          />
          <button class="notes-add-btn" @click=${() => this._addNote(call)}
            ?disabled=${!this._noteText.trim()}>
            <i class="fa-sharp fa-light fa-plus"></i>
          </button>
        </div>
      </div>
    `;
  }

  _formatNoteTime(timestamp) {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-US', { timeZone: tz.tz, hour: 'numeric', minute: '2-digit' });
  }

  // ── Flags ──

  _getCallFlag(call) {
    const meta = call.metadata || {};
    const flags = meta.flags || [];
    return flags.length > 0 ? flags[0] : null;
  }

  _toggleFlagMenu(e, call) {
    e.stopPropagation();
    this._showFlagMenu = !this._showFlagMenu;
    this._showNotes = false;
  }

  async _setFlag(call, type) {
    this._flagging = true;
    this._showFlagMenu = false;
    try {
      await callsService.addFlag(call.id, type);
    } catch (e) { /* silent */ }
    this._flagging = false;
  }

  async _removeFlag(call) {
    this._flagging = true;
    this._showFlagMenu = false;
    try {
      await callsService.removeFlag(call.id);
    } catch (e) { /* silent */ }
    this._flagging = false;
  }

  _renderFlagMenu(call) {
    const hasFlag = this._getCallFlag(call);
    const flagTypes = [
      { type: 'important', label: 'Important', icon: 'fa-star', color: '#D97706' },
      { type: 'followup', label: 'Follow Up', icon: 'fa-clock', color: '#2563EB' },
      { type: 'issue', label: 'Issue', icon: 'fa-circle-exclamation', color: '#DC2626' },
      { type: 'spam', label: 'Spam', icon: 'fa-ban', color: '#6B7280' }
    ];

    return html`
      <div class="flag-menu">
        ${flagTypes.map(f => html`
          <button class="flag-menu-item" @click=${() => this._setFlag(call, f.type)}>
            <i class="fa-sharp fa-light ${f.icon}" style="color: ${f.color}"></i>
            ${f.label}
          </button>
        `)}
        ${hasFlag ? html`
          <div class="flag-menu-divider"></div>
          <button class="flag-menu-item flag-remove" @click=${() => this._removeFlag(call)}>
            <i class="fa-sharp fa-light fa-xmark"></i>
            Remove Flag
          </button>
        ` : ''}
      </div>
    `;
  }

  // ── Hangup ──

  async _confirmHangup(call) {
    const confirmed = await confirmService.show({
      title: 'End this call?',
      icon: 'fa-phone-hangup',
      description: `This will immediately disconnect the caller from ${call?.agent_name || 'AI Agent'}.`,
      confirmText: 'End Call',
    });
    if (!confirmed) return;
    await this._hangupCall(call);
  }

  async _hangupCall(call) {
    if (!call?.call_sid || this._hangingUp) return;
    this._hangingUp = true;
    try {
      const result = await twilioService.hangupCall(call.call_sid);
      if (result.success) {
        // Optimistically remove the call from the live list
        liveCallsService.removeCall(call.id);
        this._selectedCallId = null;
        this.dispatchEvent(new CustomEvent('toast', {
          bubbles: true, composed: true,
          detail: { message: 'Call ended successfully', type: 'success' }
        }));
      } else {
        this.dispatchEvent(new CustomEvent('toast', {
          bubbles: true, composed: true,
          detail: { message: result.error || 'Failed to end call', type: 'error' }
        }));
      }
    } catch (e) {
      this.dispatchEvent(new CustomEvent('toast', {
        bubbles: true, composed: true,
        detail: { message: 'Failed to end call', type: 'error' }
      }));
    } finally {
      this._hangingUp = false;
    }
  }

  // ── Helpers ──

  /** Calculate live duration in seconds from started_at ISO string */
  _calcDuration(startedAt) {
    if (!startedAt) return 0;
    const start = new Date(startedAt).getTime();
    if (isNaN(start)) return 0;
    return Math.max(0, Math.floor((this._now - start) / 1000));
  }

  _formatDuration(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  _formatTime(isoString) {
    if (!isoString) return '--:--';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '--:--';
    return date.toLocaleTimeString('en-US', {
      timeZone: tz.tz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  /** Convert timestamp_ms (epoch ms) to mm:ss relative to call start */
  _formatTimestampMs(ms, startedAt) {
    if (!ms && ms !== 0) return '';
    if (startedAt) {
      const startMs = new Date(startedAt).getTime();
      if (!isNaN(startMs)) ms = ms - startMs;
    }
    if (ms < 0) ms = 0;
    const totalSec = Math.floor(ms / 1000);
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  // ── Pipeline State Display ──

  /**
   * Get pipeline state category for CSS class.
   * @param {string} state - e.g. 'listening', 'processing', 'speaking', 'tool:check_availability'
   * @returns {'listening'|'processing'|'speaking'|'tool'}
   */
  _getPipelineCategory(state) {
    if (!state) return 'listening';
    if (state.startsWith('tool:')) return 'tool';
    if (['listening', 'processing', 'speaking'].includes(state)) return state;
    return 'listening';
  }

  /**
   * Human-readable label for pipeline state.
   */
  _formatPipelineState(state) {
    if (!state) return 'Listening';
    if (state === 'listening') return 'Listening';
    if (state === 'processing') return 'Thinking';
    if (state === 'speaking') return 'Speaking';
    if (state.startsWith('tool:')) {
      const toolName = state.slice(5);
      return this._formatToolName(toolName);
    }
    return state;
  }

  /**
   * Human-readable tool name.
   */
  _formatToolName(name) {
    const toolNames = {
      check_availability: 'Checking availability',
      create_reservation: 'Creating reservation',
      lookup_customer: 'Looking up customer',
      send_payment_link: 'Sending payment link',
      send_sms: 'Sending SMS',
      end_call: 'Ending call'
    };
    return toolNames[name] || name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  /**
   * Render pipeline state badge.
   * @param {string} state
   * @param {boolean} compact - true for call card, false for detail
   */
  _renderPipelineBadge(state, compact) {
    const category = this._getPipelineCategory(state);
    const label = this._formatPipelineState(state);
    const icon = {
      listening: 'fa-ear-listen',
      processing: 'fa-brain',
      speaking: 'fa-volume-high',
      tool: 'fa-wrench'
    }[category];

    return html`
      <div class="pipeline-badge pipeline-${category} ${compact ? 'pipeline-compact' : ''}">
        <i class="fa-sharp fa-light ${icon}"></i>
        <span class="pipeline-label">${label}</span>
        <span class="pipeline-dots">
          <span class="pipeline-dot"></span>
          <span class="pipeline-dot"></span>
          <span class="pipeline-dot"></span>
        </span>
      </div>
    `;
  }

  /** Format phone number for display */
  _formatPhone(phone) {
    if (!phone) return 'Unknown';
    // Browser/Agent One PTT calls — no real phone number
    if (phone === 'testing' || phone === 'In-App Call') return 'In-App Call';
    // If already formatted or short, return as-is
    if (phone.includes('(') || phone.length < 10) return phone;
    // Strip +1 prefix for US numbers and format
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      const area = digits.slice(1, 4);
      const mid = digits.slice(4, 7);
      const last = digits.slice(7);
      return `(${area}) ${mid}-${last}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  }
}

customElements.define('live-calls-monitor', LiveCallsMonitor);
